/**
 * useDerivedFacets - Hook to derive facets from mock deals based on current context
 *
 * CRITICAL: This hook is ONLY active in MOCK mode.
 * In DB mode, it returns empty results since facets come from rpc_get_discover_facets_v3.
 *
 * SUPPORTS (Mock mode only):
 * - Multi-industry selection (UNION of categories)
 * - Query preview for facets (Option 1)
 * - Order-independent matching
 *
 * @version 3.0.0
 */
import { useMemo } from 'react';
import { isDbMode, isMockMode } from '../config/dataMode';
import {
  buildMockFacets,
  type FilterContext,
  type DerivedFacets,
  type FacetItem,
} from '../mock/discoveryMockData';

export interface UseDerivedFacetsOptions {
  query?: string;
  /** Multi-select industry IDs */
  industryIds?: string[];
  /** Multi-select category IDs */
  categoryIds?: string[];
  /** Multi-select subcategory IDs */
  subcategoryIds?: string[];
  commissionMin?: number;
  commissionMax?: number;
  priceMin?: number;
  priceMax?: number;
  discountMin?: number;
  discountMax?: number;
}

export interface UseDerivedFacetsReturn extends DerivedFacets {
  // Convenience props
  hasIndustry: boolean;
  hasCategory: boolean;
  hasSubcategory: boolean;
  currentLevel: 'industry' | 'category' | 'subcategory';
  /** Set of industry IDs that have matching deals in current context */
  availableIndustryIds: Set<string>;
}

// Empty result for DB mode
const EMPTY_FACETS: DerivedFacets = {
  deals: [],
  industryFacets: [],
  categoryFacets: [],
  subcategoryFacets: [],
  availableIndustryIds: new Set<string>(),
};

/**
 * Hook that derives facets from mock deals based on current filter context.
 *
 * CRITICAL: In DB mode, returns empty results. Facets must come from DB RPCs.
 *
 * MEMOIZATION: Uses a stable context key including ALL relevant fields.
 * Any change to query, taxonomy, or ranges will recompute facets.
 */
export function useDerivedFacets(options: UseDerivedFacetsOptions): UseDerivedFacetsReturn {
  const {
    query,
    industryIds = [],
    categoryIds = [],
    subcategoryIds = [],
    commissionMin,
    commissionMax,
    priceMin,
    priceMax,
    discountMin,
    discountMax,
  } = options;

  // Build a stable context key for memoization
  // CRITICAL: Must include ALL relevant fields to prevent staleness
  const ctxKey = useMemo(
    () =>
      JSON.stringify({
        query: query || '',
        industryIds: [...industryIds].sort(),
        categoryIds: [...categoryIds].sort(),
        subcategoryIds: [...subcategoryIds].sort(),
        commissionMin: commissionMin ?? 0,
        commissionMax: commissionMax ?? 100,
        priceMin: priceMin ?? 0,
        priceMax: priceMax ?? 1000,
        discountMin: discountMin ?? 0,
        discountMax: discountMax ?? 100,
      }),
    [
      query,
      industryIds,
      categoryIds,
      subcategoryIds,
      commissionMin,
      commissionMax,
      priceMin,
      priceMax,
      discountMin,
      discountMax,
    ],
  );

  // Derive facets with proper memoization
  // CRITICAL: In DB mode, return empty results to prevent mock/DB hybrid
  const derivedFacets = useMemo(() => {
    // In DB mode, do NOT derive from mock data
    if (isDbMode()) {
      return EMPTY_FACETS;
    }

    // Mock mode: derive from mock dataset
    const ctx: FilterContext = {
      query,
      industryIds,
      categoryIds,
      subcategoryIds,
      commissionMin,
      commissionMax,
      priceMin,
      priceMax,
      discountMin,
      discountMax,
    };
    return buildMockFacets(ctx);
  }, [ctxKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasIndustry = industryIds.length > 0;
  const hasCategory = categoryIds.length > 0;
  const hasSubcategory = subcategoryIds.length > 0;

  const currentLevel: 'industry' | 'category' | 'subcategory' = !hasIndustry
    ? 'industry'
    : !hasCategory
      ? 'category'
      : 'subcategory';

  return {
    ...derivedFacets,
    hasIndustry,
    hasCategory,
    hasSubcategory,
    currentLevel,
  };
}

export default useDerivedFacets;
